import React, { useState } from "react";
import { Radio, Youtube, Calendar as CalIcon, ExternalLink } from "lucide-react";

// You can replace this with a real YouTube live URL when broadcasting.
// While no live stream is running, YouTube shows the channel's latest content.
const DEFAULT_CHANNEL_EMBED = "https://www.youtube.com/embed/live_stream?channel=UCBR8-60-B28hp2BmDPdntcQ";

const UPCOMING = [
  {
    title: "Buddha Purnima 2026 — Live from Stockport",
    date: "2 May 2026, 10.30 BST",
    note: "Tisarana, mettā meditation, community lunch, lamp lighting.",
  },
  {
    title: "Bhima Koregaon Shourya Diwas Memorial",
    date: "1 January 2027, 14.00 GMT",
    note: "Reading of the names · global lamp ceremony.",
  },
  {
    title: "Babasaheb Jayanti Special",
    date: "14 April 2027, 18.00 BST",
    note: "Talks from chapters across the UK and the world.",
  },
];

export default function PlatformLive() {
  const [embedUrl, setEmbedUrl] = useState(DEFAULT_CHANNEL_EMBED);
  const [customId, setCustomId] = useState("");

  const useCustom = (e) => {
    e.preventDefault();
    if (!customId.trim()) return;
    // Accept either a full URL or a video ID
    const id = customId.trim();
    let v = id;
    const m = id.match(/(?:v=|youtu\.be\/|\/live\/)([a-zA-Z0-9_-]{6,})/);
    if (m) v = m[1];
    setEmbedUrl(`https://www.youtube.com/embed/${v}?autoplay=1`);
  };

  return (
    <div className="px-4 sm:px-8 lg:px-10 py-8 max-w-[1500px] mx-auto" data-testid="platform-live">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-saffron">Live · Global Sangha</p>
          <h1 className="font-display text-4xl text-navy">Live Events</h1>
          <p className="mt-1 text-navy/65 max-w-2xl">
            Watch global broadcasts, Buddha Purnima, Babasaheb Jayanti, Dhamma talks, live and free.
            Powered by YouTube · unlimited audience.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="rounded-xl overflow-hidden border border-navy/10 bg-black aspect-video shadow-sm" data-testid="live-embed">
            <iframe
              src={embedUrl}
              title="ABGN-UK Live Stream"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          <form onSubmit={useCustom} className="mt-4 flex flex-wrap items-center gap-2">
            <Youtube size={18} className="text-red-600" />
            <input
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              placeholder="Paste a YouTube live URL or video ID to watch a specific stream"
              data-testid="live-custom-input"
              className="form-input flex-1 min-w-[260px]"
            />
            <button
              type="submit"
              data-testid="live-custom-load"
              className="px-4 py-2 bg-navy text-pearl rounded-md text-sm hover:bg-navy-light"
            >
              Load
            </button>
          </form>
          <p className="mt-3 text-xs text-navy/55">
            <strong>Tip for hosts:</strong> "Go Live" from any YouTube channel ABGN-UK admins, then paste
            the live URL above. Members anywhere in the world watch instantly, no apps to install,
            no audience cap.
          </p>
        </div>

        <aside className="space-y-4" data-testid="live-upcoming">
          <h2 className="font-display text-xl text-navy flex items-center gap-2">
            <CalIcon size={18} className="text-saffron" /> Upcoming broadcasts
          </h2>
          {UPCOMING.map((u) => (
            <div key={u.title} className="bg-white border border-navy/10 p-4 rounded-lg shadow-sm">
              <div className="text-[10px] uppercase tracking-widest text-saffron">{u.date}</div>
              <div className="font-display text-base text-navy mt-1">{u.title}</div>
              <div className="text-xs text-navy/65 mt-1.5 leading-relaxed">{u.note}</div>
            </div>
          ))}
          <a
            href="https://www.youtube.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center mt-2 text-xs text-saffron hover:text-saffron-light"
            data-testid="live-channel-link"
          >
            Open ABGN-UK channel on YouTube <ExternalLink size={11} className="inline" />
          </a>
        </aside>
      </div>
    </div>
  );
}
